import { useState } from 'react';

import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import { SSRConfig } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useForm } from 'react-hook-form';

import { cleanObject } from '@/lib/utils';
import Layout from '@/modules/core/components/Layout';

interface TayaraLeadsData {
  name: string;
  mobile: string;
  email: string;
  activity: string;
}

const TayaraLeadsPage = () => {
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { register, handleSubmit, reset } = useForm<TayaraLeadsData>({
    defaultValues: {},
  });

  const onSubmit = async (data: TayaraLeadsData) => {
    setIsPosting(true);
    await fetch('/api/tayara-pro-form/', {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...data,
      }),
    });
    setIsSubmitted(true);
    setIsPosting(false);
    reset();
  };

  return (
    <div className="m-auto">
      <div className="max-w-xl m-auto mb-32">
        {isSubmitted ? (
          <div className="flex flex-col gap-5 items-center justify-center mt-24">
            <img
              src="/media/t-celebrate.svg"
              alt="tayara illustration: celebrate"
            ></img>
            <h3 className="text-2xl text-gray-800 text-center">
              Merci, un de nos agents vous contactera dans les plus brefs délais
            </h3>
          </div>
        ) : (
          <form className="flex flex-col m-5" onSubmit={handleSubmit(onSubmit)}>
            <h1 className="text-3xl font-bold mb-20 text-center">
              Demande de contact PRO
            </h1>
            <div className="mb-6">
              <div className="mb-2">
                <label
                  className="block text-gray-800 font-bold"
                  htmlFor="last-name"
                >
                  Nom et prénom
                </label>
              </div>
              <div>
                <input
                  className="bg-gray-200/50 appearance-none border-2 border-gray-200/50 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-gray-500/20"
                  id="last-name"
                  type="text"
                  autoFocus
                  {...register('name')}
                  required
                />
              </div>
            </div>
            <div className="mb-6">
              <div className="mb-2">
                <label
                  className="block text-gray-800 font-bold"
                  htmlFor="mobile"
                >
                  N° Tél. Mobile
                </label>
              </div>
              <div>
                <input
                  className="bg-gray-200/50 appearance-none border-2 border-gray-200/50 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-gray-500/20"
                  id="mobile"
                  type="tel"
                  pattern="([0-9]{2}\s?[0-9]{3}\s?[0-9]{3})|([0-9]{2}\s?[0-9]{2}\s?[0-9]{2}\s?[0-9]{2}\s?)"
                  {...register('mobile')}
                  required
                />
              </div>
            </div>
            <div className="mb-6">
              <div className="mb-2">
                <label
                  className="block text-gray-800 font-bold"
                  htmlFor="mobile"
                >
                  Email
                </label>
              </div>
              <div>
                <input
                  className="bg-gray-200/50 appearance-none border-2 border-gray-200/50 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-gray-500/20"
                  id="email"
                  type="email"
                  {...register('email')}
                  required
                />
              </div>
            </div>
            <div className="mb-6">
              <div className="mb-2">
                <label
                  className="block text-gray-800 font-bold"
                  htmlFor="mobile"
                >
                  Activité
                </label>
              </div>
              <div>
                <select
                  className="bg-gray-200/50 border-2 border-gray-200/50 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-gray-500/20"
                  id="activity"
                  {...register('activity')}
                  required
                >
                  <option value="Automobile">Automobile</option>
                  <option value="Immobilier">Immobilier</option>
                  <option value="Meubles et déco">Meubles et déco</option>
                  <option value="Emploi et services">Emploi et services</option>
                  <option value="Accessoires auto">Accessoires auto</option>
                  <option value="Brand/Marque">Brand/Marque</option>
                  <option value="Autres">Autres</option>
                </select>
              </div>
            </div>
            <div className="mt-8">
              <div>
                <button
                  className={`btn btn-primary rounded w-full ${
                    isPosting && 'loading'
                  }`}
                  type="submit"
                  disabled={isPosting}
                >
                  Envoyer
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TayaraLeadsPage;

// Dynamic layout configuration
TayaraLeadsPage.getLayout = function getLayout(page: JSX.Element) {
  return (
    <Layout>
      <Layout.Wide>{page}</Layout.Wide>
    </Layout>
  );
};

// Server side code and i18n
export const getServerSideProps: GetServerSideProps = async ({
  locale,
}): Promise<GetServerSidePropsResult<SSRConfig>> => ({
  props: cleanObject({
    ...(await serverSideTranslations(locale || 'en')),
  }),
});
